// app/src/modules/ui/SystemError.tsx — every full-page error surface in one place.
// Each kind explains WHAT happened, WHAT IT MEANS for the person's work (is saved
// data safe? does the till keep working? is retrying safe?), and offers actions.
// Inline token styles with hard fallbacks so the crash page renders even when the
// stylesheet failed to load.
import React from "react";
import { ApiError, OfflineError } from "../../lib/api";
import Badge from "./Badge";

export type ErrorKind =
  | "not_found" | "crash" | "offline" | "server" | "edge" | "rate_limited" | "forbidden" | "db_unavailable";

const COPY: Record<ErrorKind, { code: string; tone: "neutral" | "warning" | "danger" | "info"; title: string; what: string; effects: string[] }> = {
  not_found: {
    code: "HTTP 404", tone: "neutral", title: "That page doesn't exist",
    what: "The address doesn't match any screen — it may have been mistyped, or the link is out of date.",
    effects: ["Nothing was changed or lost.", "Your data and any open work are untouched."],
  },
  crash: {
    code: "APP ERROR", tone: "danger", title: "This screen hit a problem",
    what: "The app ran into an unexpected error while drawing this screen.",
    effects: [
      "Anything already saved (sales, jobs, invoices) is safe on the server.",
      "Unsaved edits on this screen may be lost when you reload.",
      "Other screens and other tills are unaffected.",
    ],
  },
  offline: {
    code: "NO CONNECTION", tone: "warning", title: "You're offline",
    what: "The device can't reach the internet right now.",
    effects: [
      "The till keeps working — sales are queued on this device and sync automatically when the connection returns.",
      "Reports, search and anything needing live data will be stale or unavailable until you're back online.",
      "Don't clear the browser/app data while offline: queued sales live on this device.",
    ],
  },
  server: {
    code: "HTTP 500", tone: "danger", title: "Server error",
    what: "The system hit an internal error handling that request. It has been logged.",
    effects: [
      "The action you just tried was NOT applied — retry once; if it fails again, wait a minute.",
      "Everything saved before this is safe.",
      "If it keeps happening, note the time and tell Altaf — the logs will show the cause.",
    ],
  },
  edge: {
    code: "CLOUDFLARE 5xx", tone: "warning", title: "Network edge problem",
    what: "Cloudflare (which sits in front of the system) couldn't complete the request — typically a brief network wobble between the edge and the server (errors 520–526 or 1101).",
    effects: [
      "This is almost always temporary — retry in a few seconds.",
      "The action was NOT applied; retrying is safe.",
      "The till's offline queue still protects sales if it persists.",
    ],
  },
  rate_limited: {
    code: "HTTP 429", tone: "warning", title: "Slow down a moment",
    what: "Too many requests in a short burst, so the system briefly paused this device.",
    effects: ["Nothing was saved from the blocked attempt.", "Wait ~30–60 seconds and try again — no other fix needed."],
  },
  forbidden: {
    code: "HTTP 403", tone: "neutral", title: "You don't have access to this",
    what: "Your account's role doesn't permit this screen or action.",
    effects: ["No changes were made.", "An owner can grant access under Staff if you need it."],
  },
  db_unavailable: {
    code: "HTTP 503", tone: "warning", title: "Database briefly unavailable",
    what: "The database is momentarily locked (this happens for a few seconds during a backup/export).",
    effects: [
      "Retry in a few seconds — it recovers on its own.",
      "The action was NOT applied; retrying is safe.",
      "Nothing already saved is at risk.",
    ],
  },
};

/** Map a thrown error to the right page/banner kind. */
export function classifyError(e: unknown): ErrorKind {
  if (e instanceof OfflineError) return "offline";
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";
  if (e instanceof ApiError) {
    if (e.code === "edge") return "edge";
    if (e.status === 429) return "rate_limited";
    if (e.status === 403) return "forbidden";
    if (e.status === 404) return "not_found";
    if (e.status === 503 || e.code === "db_unavailable") return "db_unavailable";
    if (e.status >= 520 && e.status <= 530) return "edge";
    if (e.status >= 500) return "server";
  }
  return "server";
}

export function SystemErrorPage({ kind, detail, onRetry, homeHref = "/" }: {
  kind: ErrorKind; detail?: string; onRetry?: () => void; homeHref?: string;
}) {
  const c = COPY[kind];
  const toneMap = { neutral: "neutral", warning: "warning", danger: "danger", info: "info" } as const;
  return (
    <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", padding: 24,
                  background: "var(--paper, #F6F8FB)", color: "var(--ink, #0E1116)", fontFamily: "var(--body, system-ui, sans-serif)" }}>
      <div style={{ maxWidth: 460, width: "100%", background: "var(--card, #fff)", border: "1px solid var(--line, rgba(14,17,22,.08))",
                    borderRadius: "var(--r-2xl, 8px)", boxShadow: "var(--shadow-md, 0 12px 28px rgba(14,17,22,.09))", padding: "28px 26px", display: "grid", gap: 14 }}>
        <div><Badge tone={toneMap[c.tone]}>{c.code}</Badge></div>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.02em", margin: 0, lineHeight: 1.1 }}>{c.title}</h1>
        <p style={{ margin: 0, fontSize: 14, color: "var(--ink-2, #3A424C)", lineHeight: 1.55 }}>{c.what}</p>
        <div style={{ background: "var(--paper-2, #EAEEF3)", borderRadius: "var(--r-lg, 5px)", padding: "12px 14px", display: "grid", gap: 7 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted, #6B7280)" }}>What this means</div>
          {c.effects.map((t, i) => (
            <div key={i} style={{ fontSize: 13, color: "var(--ink-2, #3A424C)", lineHeight: 1.5, display: "flex", gap: 8 }}>
              <span aria-hidden="true" style={{ color: "var(--muted, #6B7280)" }}>·</span><span>{t}</span>
            </div>
          ))}
        </div>
        {detail && <div style={{ fontSize: 12, color: "var(--muted, #6B7280)", fontFamily: "var(--mono, monospace)", wordBreak: "break-word" }}>{detail}</div>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 2 }}>
          <button type="button" onClick={onRetry ?? (() => window.location.reload())}
            style={{ flex: 1, minWidth: 130, background: "var(--accent, #BB2D22)", color: "var(--accent-ink, #fff)", borderRadius: "var(--r-lg, 5px)", padding: "11px 16px", fontWeight: 800, fontSize: 14 }}>
            {kind === "offline" ? "Try again" : "Retry"}
          </button>
          <button type="button" onClick={() => { window.location.href = homeHref; }}
            style={{ flex: 1, minWidth: 130, background: "transparent", color: "var(--ink, #0E1116)", border: "1.5px solid var(--line-strong, rgba(14,17,22,.14))", borderRadius: "var(--r-lg, 5px)", padding: "11px 16px", fontWeight: 700, fontSize: 14 }}>
            Go to home
          </button>
        </div>
      </div>
    </div>
  );
}

/** Catches render-time crashes anywhere below it and shows the crash page instead of a blank screen. */
export class AppErrorBoundary extends React.Component<{ children: React.ReactNode; homeHref?: string }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) { console.error("render crash:", error, info.componentStack); }
  render() {
    if (this.state.error) {
      return <SystemErrorPage kind="crash" homeHref={this.props.homeHref}
        detail={String(this.state.error?.message ?? this.state.error).slice(0, 200)}
        onRetry={() => { this.setState({ error: null }); window.location.reload(); }} />;
    }
    return this.props.children;
  }
}
